import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSilver } from "@/context/SilverContext";
import { useCampaignLabels } from "@/components/silver/builder/useCampaignLabels";
import { FreeSampleOption } from "@/components/silver/builder/SampleFan";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";
import { areasText } from "@/components/silver/results/AudienceHeadline";
import { formatGBP } from "@/config/pricing";
import { buildCampaignPayload, formatApiError, submitSilverCampaign } from "@/lib/submitSilverCampaign";
import { CONTACT } from "@/data/content";
import { cn } from "@/lib/utils";
import { flush, funnel, track } from "@/lib/analytics";

const FIELDS = [
  { id: "firstName", label: "First name", auto: "given-name", required: true, half: true },
  { id: "lastName", label: "Last name", auto: "family-name", required: true, half: true },
  { id: "agency", label: "Estate agency", auto: "organization", required: true, half: true },
  { id: "branch", label: "Branch", auto: "off", required: false, half: true },
  { id: "email", label: "Email", auto: "email", type: "email", required: true },
  { id: "telephone", label: "Telephone", auto: "tel", type: "tel", required: true },
];

const EMPTY = { firstName: "", lastName: "", agency: "", branch: "", email: "", telephone: "", message: "", contactConsent: false };

function validate(v) {
  const e = {};
  FIELDS.forEach((f) => { if (f.required && !v[f.id].trim()) e[f.id] = `${f.label} is required`; });
  if (v.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "Enter a valid email address";
  if (v.telephone && v.telephone.replace(/[^0-9]/g, "").length < 10) e.telephone = "Enter a valid telephone number";
  if (!v.contactConsent) e.contactConsent = "Please confirm so the Print team can contact you";
  return e;
}

function BriefStrip({ labels }) {
  const { audience, price, freeSample } = useSilver();
  const bits = [areasText(audience.areas), `${fmtInt(audience.total)} households`, labels.formatLabel, labels.creativeLabel, `from ${formatGBP(price.baseCost)}`].filter(Boolean);
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="enquiry-brief-strip">
      {bits.map((b) => <span key={b} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-ui text-[11px] font-semibold text-white/75">{b}</span>)}
      {freeSample && <span className="rounded-full border border-[#96C11F]/60 bg-[#96C11F]/15 px-3 py-1 font-ui text-[11px] font-semibold text-[#C6EE5A]" data-testid="enquiry-free-sample-chip">+ Free sample pack</span>}
    </div>
  );
}

export function EnquiryForm({ onSending }) {
  const ctx = useSilver();
  const { freeSample, setFreeSample } = ctx;
  const labels = useCampaignLabels();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const focusField = (field) => { funnel("enquiry_started"); track("form", "field_focus", { field }); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    track("form", "submit_attempt", { errors: Object.keys(errs).map((k) => (k === "contactConsent" ? "consent" : k)) });
    if (Object.keys(errs).length) return;
    setStatus("sending");
    onSending?.(true);
    const customer = { ...values, branch: values.branch || null, message: values.message || null, freeSample };
    const payload = buildCampaignPayload({ audience: ctx.audience, filters: ctx.filters, audienceLabel: ctx.audienceLabel, campaign: labels, price: ctx.price, customer });
    try {
      const [res] = await Promise.all([submitSilverCampaign(payload), new Promise((r) => setTimeout(r, 1400))]);
      setStatus("done");
      track("form", "submit_ok", { ref: res.reference });
      funnel("enquiry_submitted");
      flush();
      setTimeout(() => ctx.setSubmission({ ...res, payload }), 900);
    } catch (err) {
      setStatus("idle");
      onSending?.(false);
      track("form", "submit_fail");
      toast.error(formatApiError(err.response?.data?.detail) || `We couldn't send your brief. Please call ${CONTACT.phone}.`);
    }
  };

  return (
    <motion.form key="enquire" onSubmit={submit} noValidate initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -40, opacity: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="p-6 sm:p-8" data-testid="enquiry-form">
      <p className="eyebrow">Request a quotation</p>
      <h3 className="mt-2 text-3xl font-extrabold tracking-[-0.04em]">Send your campaign brief.</h3>
      <p className="mt-2 text-[13px] text-white/55">Your audience and campaign choices are attached automatically.</p>
      <BriefStrip labels={labels} />

      <div className="mt-6 lg:hidden">
        <FreeSampleOption value={freeSample} onChange={setFreeSample} />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <label key={f.id} className={cn("block", f.half ? "col-span-2 sm:col-span-1" : "col-span-2")}>
            <span className="text-[12px] font-semibold text-white/75">{f.label}{!f.required && <span className="font-normal text-white/40"> (optional)</span>}</span>
            <input type={f.type || "text"} autoComplete={f.auto} value={values[f.id]} onFocus={() => focusField(f.id)} onChange={(e) => set(f.id, e.target.value)} aria-invalid={!!errors[f.id]}
              className={cn("field mt-1.5 w-full px-3.5 py-3 text-[14px]", errors[f.id] && "!border-[#EF4444]/70")} data-testid={`enquiry-${f.id}-input`} />
            {errors[f.id] && <span className="mt-1 block text-[11px] text-[#FCA5A5]" data-testid={`enquiry-${f.id}-error`}>{errors[f.id]}</span>}
          </label>
        ))}
        <label className="col-span-2 block">
          <span className="text-[12px] font-semibold text-white/75">Message <span className="font-normal text-white/40">(optional)</span></span>
          <textarea rows={3} value={values.message} onFocus={() => focusField("message")} onChange={(e) => set("message", e.target.value)} className="field mt-1.5 w-full resize-none px-3.5 py-3 text-[14px]" data-testid="enquiry-message-input" />
        </label>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3 text-[13px] text-white/75">
        <button type="button" role="checkbox" aria-checked={values.contactConsent} onClick={() => { focusField("consent"); set("contactConsent", !values.contactConsent); }}
          className={cn("mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors", values.contactConsent ? "border-[#96C11F] bg-[#96C11F] text-[#12290F]" : "border-white/30")} data-testid="enquiry-consent-checkbox">
          {values.contactConsent && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </button>
        <span>I'd like the NicheCom Print team to contact me about this campaign.</span>
      </label>
      {errors.contactConsent && <p className="mt-1 pl-8 text-[11px] text-[#FCA5A5]" data-testid="enquiry-consent-error">{errors.contactConsent}</p>}

      <motion.button type="submit" disabled={status !== "idle"} layout
        className={cn("btn-primary relative mt-6 w-full overflow-hidden !py-4 text-[16px]", status === "done" && "!rounded-full")}
        data-testid="enquiry-submit-btn">
        {status === "idle" && <>{freeSample ? "Send brief & request samples" : "Send campaign brief"} <ArrowRight className="h-5 w-5" /></>}
        {status === "sending" && (
          <>
            <motion.span className="absolute inset-y-0 left-0 bg-[#C6EE5A]/40" initial={{ width: "0%" }} animate={{ width: "92%" }} transition={{ duration: 1.6, ease: "easeOut" }} />
            <span className="relative flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Sending brief…</span>
          </>
        )}
        {status === "done" && (
          <motion.span className="flex items-center gap-2" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 18 }}>
            <Check className="h-5 w-5" strokeWidth={3} /> Brief sent
          </motion.span>
        )}
      </motion.button>
      <div className="mt-4 flex flex-col items-center gap-2 text-[11px] text-white/35 sm:flex-row sm:justify-between">
        <p>We'll only use your details to discuss this campaign.</p>
        <button type="button" onClick={() => ctx.setBuilderMode("build")} className="inline-flex items-center gap-1.5 text-[12px] text-white/50 transition-colors hover:text-white" data-testid="enquiry-back-btn">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to campaign
        </button>
      </div>
    </motion.form>
  );
}
